/**
 * Total Bonus, worked out from the two numbers it has always been the product
 * of.
 *
 * Feedback #39. `total_bonus = bonus_per_acre × deal_net_acres_delivered` holds
 * for 427 of 427 deals currently carrying all three values — exactly, to the
 * cent, with no exceptions. Nothing computed it: the column is plain and
 * nullable on both sides, so that agreement is a month of people doing the
 * multiplication by hand.
 *
 * Still a DEFAULT, not a computed column. It fills on edit and the user can
 * type over it, because the one thing the data cannot tell us is whether a
 * future deal will need a negotiated total that is not the product.
 */

const isBlank = (v) => v === null || v === undefined || v === "";

const numOrNull = (v) => {
  if (isBlank(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** The deal columns this derivation reads. */
export const BONUS_INPUT_FIELDS = ["bonus_per_acre", "deal_net_acres_delivered"];

/**
 * `deals.total_bonus` is numeric(14,2), so the form should not offer more
 * precision than the column can store. Rounded through a scaled integer
 * because 0.1 * 3 is 0.30000000000000004 and a bonus that reads
 * $30.000000000000004 in the input would be saved as typed.
 */
const toCents = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * @returns {number|null} the product, or null when either input is missing or
 *   unusable — in which case the caller leaves the field alone rather than
 *   blanking a total somebody entered.
 */
export const totalBonusFrom = (bonusPerAcre, netAcres) => {
  const bpa = numOrNull(bonusPerAcre);
  const acres = numOrNull(netAcres);
  if (bpa === null || acres === null) return null;
  return toCents(bpa * acres);
};

/**
 * What the deal form should fill in after a change.
 *
 * @param {object} changed  antd's `changedValues`
 * @param {object} all      antd's `allValues`
 * @returns {object} a patch for `form.setFieldsValue`, empty when there is
 *   nothing to do.
 */
export const deriveDealTotals = (changed = {}, all = {}) => {
  const touched = BONUS_INPUT_FIELDS.some((f) =>
    Object.prototype.hasOwnProperty.call(changed, f)
  );
  if (!touched) return {};

  const total = totalBonusFrom(all.bonus_per_acre, all.deal_net_acres_delivered);
  return total === null ? {} : { total_bonus: total };
};

export default deriveDealTotals;
